/**
 * Home page API functions (specs/home-page). Thin fetchers over `/home/*`; React Query hooks wrap them.
 */

import type { AxiosResponse } from 'axios';
import { API_ENDPOINTS } from '../../../constants/api';
import type { TodaySlateView, WeeklyQuestsView } from '../types/home.types';

type ApiClientFn = <T = unknown>(endpoint: string, config?: object) => Promise<AxiosResponse<T>>;

interface Envelope<T> {
  success: boolean;
  data?: T;
  message?: string;
}

/** GET /home/quests — this week's quests with the viewer's progress. Throws so React Query surfaces the error state. */
export const fetchWeeklyQuests = async (apiClientFn: ApiClientFn): Promise<WeeklyQuestsView> => {
  const { data } = await apiClientFn<Envelope<WeeklyQuestsView>>(API_ENDPOINTS.home.quests, { method: 'GET' });
  if (!data?.success || !data.data) {
    throw new Error(data?.message || 'Failed to load weekly quests');
  }
  return data.data;
};

/** POST /home/quests/chest-seen — acknowledge the chest celebration. Resolves null when there is no chest (404). */
export const postChestSeen = async (apiClientFn: ApiClientFn): Promise<{ seenAt: string } | null> => {
  try {
    const { data } = await apiClientFn<Envelope<{ seenAt: string }>>(API_ENDPOINTS.home.questsChestSeen, {
      method: 'POST',
      data: {},
    });
    return data?.success && data.data ? data.data : null;
  } catch (error) {
    const status = (error as { response?: { status?: number } }).response?.status;
    if (status === 404) return null;
    throw error;
  }
};

/** GET /home/slate/today — today's prepared challenge. Throws so React Query surfaces the error state. */
export const fetchTodaySlate = async (apiClientFn: ApiClientFn): Promise<TodaySlateView> => {
  const { data } = await apiClientFn<Envelope<TodaySlateView>>(API_ENDPOINTS.home.slateToday, { method: 'GET' });
  if (!data?.success || !data.data) {
    throw new Error(data?.message || "Failed to load today's challenge");
  }
  return data.data;
};
